import React from "react";
import { Collapse, Drawer } from "antd";
import dayjs from "dayjs";
import { AN_COLORS, AN_FONT, AN_MONO, anAlpha } from "../../../design-system/tokens";
import {
  actorLabel,
  describeAction,
  describeTarget,
  diffChanges,
  formatLeaf,
  hasValue,
  pretty,
  targetTypeLabel,
} from "./auditLogModel";
import { ResultPill } from "./auditLogUi";

// Fields shown in full underneath the diff rather than flattened row by row
// (only their changed leaves appear in the table).
const COLLAPSED_KEYS = ["policy"];

const sectionTitle = { margin: "0 0 0.625rem", fontSize: "0.875rem", fontWeight: 600, color: AN_COLORS.ink };
const mono = { fontFamily: AN_MONO, fontSize: "0.75rem", overflowWrap: "anywhere", wordBreak: "break-word" };

function Field({ label, children, monospace }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "9.5rem minmax(0, 1fr)", gap: "0.75rem", padding: "0.5rem 0", borderBottom: `1px solid ${AN_COLORS.line2}` }}>
      <span style={{ fontSize: "0.8125rem", color: AN_COLORS.muted }}>{label}</span>
      <span style={{ fontSize: "0.8125rem", color: AN_COLORS.ink, minWidth: 0, overflowWrap: "anywhere", ...(monospace ? mono : null) }}>
        {children === "" || children == null ? <span style={{ color: AN_COLORS.muted }}>None</span> : children}
      </span>
    </div>
  );
}

function Value({ present, value, tone }) {
  if (!present) return <span style={{ color: AN_COLORS.muted, fontFamily: AN_FONT, fontSize: "0.75rem" }}>Not set</span>;
  const color = tone === "before" ? AN_COLORS.dangerFg : tone === "after" ? AN_COLORS.successFg : AN_COLORS.ink;
  return <span style={{ ...mono, color, whiteSpace: "pre-wrap" }}>{formatLeaf(value)}</span>;
}

function ChangesTable({ before, after }) {
  const rows = diffChanges(before, after, { collapse: COLLAPSED_KEYS });
  const changedCount = rows.filter((r) => r.changed).length;
  const cell = { padding: "0.5rem 0.625rem", minWidth: 0 };
  return (
    <div>
      <div style={{ fontSize: "0.75rem", color: AN_COLORS.muted, marginBottom: "0.5rem" }}>
        {hasValue(before) && hasValue(after)
          ? changedCount === 1
            ? "1 field changed. Changed fields are highlighted."
            : `${changedCount} fields changed. Changed fields are highlighted.`
          : hasValue(after)
          ? "Only the new values were recorded."
          : "Only the values before the change were recorded."}
      </div>
      <div role="table" aria-label="Before and after" style={{ border: `1px solid ${AN_COLORS.line}`, borderRadius: "0.625rem", overflow: "hidden" }}>
        <div
          role="row"
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 0.9fr) minmax(0, 1fr) minmax(0, 1fr)",
            background: AN_COLORS.bg,
            borderBottom: `1px solid ${AN_COLORS.line}`,
            fontSize: "0.75rem",
            fontWeight: 600,
            color: AN_COLORS.ink2,
          }}
        >
          <span role="columnheader" style={cell}>Field</span>
          <span role="columnheader" style={cell}>Before</span>
          <span role="columnheader" style={cell}>After</span>
        </div>
        {rows.length === 0 && <div style={{ ...cell, fontSize: "0.8125rem", color: AN_COLORS.muted }}>No fields recorded.</div>}
        {rows.map((r, i) => (
          <div
            role="row"
            key={r.key}
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 0.9fr) minmax(0, 1fr) minmax(0, 1fr)",
              background: r.changed ? anAlpha(AN_COLORS.warn, 10) : "transparent",
              boxShadow: r.changed ? `inset 0.1875rem 0 0 ${AN_COLORS.warn}` : "none",
              borderBottom: i === rows.length - 1 ? "none" : `1px solid ${AN_COLORS.line2}`,
            }}
          >
            <span role="cell" style={{ ...cell, ...mono, color: AN_COLORS.ink2, fontWeight: r.changed ? 600 : 400 }}>
              {r.key}
            </span>
            <span role="cell" style={cell}>
              <Value present={r.inBefore} value={r.before} tone={r.changed ? "before" : null} />
            </span>
            <span role="cell" style={cell}>
              <Value present={r.inAfter} value={r.after} tone={r.changed ? "after" : null} />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function JsonBlock({ title, value }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: "0.75rem", fontWeight: 600, color: AN_COLORS.ink2, marginBottom: "0.375rem" }}>{title}</div>
      {hasValue(value) ? (
        <pre
          style={{
            ...mono,
            margin: 0,
            padding: "0.625rem",
            maxHeight: "22rem",
            overflow: "auto",
            whiteSpace: "pre-wrap",
            background: AN_COLORS.bg,
            border: `1px solid ${AN_COLORS.line}`,
            borderRadius: "0.5rem",
            color: AN_COLORS.ink,
          }}
        >
          {pretty(value)}
        </pre>
      ) : (
        <span style={{ fontSize: "0.75rem", color: AN_COLORS.muted }}>Not recorded</span>
      )}
    </div>
  );
}

function Changes({ changes }) {
  if (!changes || typeof changes !== "object") return null;
  const { before, after } = changes;
  if (!hasValue(before) && !hasValue(after)) {
    return <JsonBlock title="Recorded changes" value={changes} />;
  }
  const side = (v, k) => (v && typeof v === "object" ? v[k] : undefined);
  const full = COLLAPSED_KEYS.filter((k) => hasValue(side(before, k)) || hasValue(side(after, k)));
  return (
    <div style={{ display: "grid", gap: "0.875rem" }}>
      <ChangesTable before={before} after={after} />
      {full.length > 0 && (
        <Collapse
          size="small"
          items={full.map((k) => ({
            key: k,
            label: k === "policy" ? "Full policy JSON" : `Full ${k} JSON`,
            children: (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(14rem, 1fr))", gap: "0.75rem" }}>
                <JsonBlock title="Before" value={side(before, k)} />
                <JsonBlock title="After" value={side(after, k)} />
              </div>
            ),
          }))}
        />
      )}
    </div>
  );
}

// Everything about one audit event, with the before/after diff when recorded.
export default function AuditEventDrawer({ event, onClose }) {
  const open = !!event;
  const e = event || {};
  const target = describeTarget(e);
  const at = e.at ? dayjs(e.at) : null;
  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="min(48rem, 100vw)"
      title={open ? <span style={{ fontFamily: AN_FONT, fontSize: "1rem", fontWeight: 600 }}>{describeAction(e)}</span> : null}
      extra={open ? <ResultPill outcome={e.outcome} /> : null}
    >
      {open && (
        <div style={{ display: "grid", gap: "1.5rem", fontFamily: AN_FONT }}>
          <section>
            <h3 style={sectionTitle}>Event</h3>
            <Field label="Time">
              {at && at.isValid() ? (
                <>
                  {at.format("D MMM YYYY, HH:mm:ss")} <span style={{ color: AN_COLORS.muted }}>({e.at})</span>
                </>
              ) : (
                e.at
              )}
            </Field>
            <Field label="Who">{actorLabel(e)}</Field>
            <Field label="User ID">{e.actorUserId ? String(e.actorUserId) : ""}</Field>
            <Field label="Action">{describeAction(e)}</Field>
            <Field label="Raw action" monospace>{e.action}</Field>
            <Field label="Request" monospace>{[e.method, e.route].filter(Boolean).join(" ")}</Field>
            <Field label="HTTP status">{e.status ? String(e.status) : ""}</Field>
            <Field label="Result"><ResultPill outcome={e.outcome} /></Field>
            <Field label="Target type">{targetTypeLabel(e.targetType)}</Field>
            <Field label="Target">{target.primary}</Field>
            <Field label="Target ID" monospace>{e.targetId}</Field>
            <Field label="Client IP" monospace>{e.clientIp}</Field>
            <Field label="User agent" monospace>{e.userAgent}</Field>
            <Field label="Tenant ID">{e.tenantId != null ? String(e.tenantId) : ""}</Field>
            <Field label="Event ID" monospace>{e.eventId}</Field>
          </section>

          <section>
            <h3 style={sectionTitle}>Changes</h3>
            {e.changes ? (
              <Changes changes={e.changes} />
            ) : (
              <div style={{ fontSize: "0.8125rem", color: AN_COLORS.muted }}>
                No before and after values were recorded for this request.
              </div>
            )}
          </section>
        </div>
      )}
    </Drawer>
  );
}
